'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { stegaClean } from 'next-sanity'

import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile'

import { subscribeToNewsletter } from '@/app/actions/subscribeToNewsletter'
import { Button, buttonSizeClasses } from '@/app/components/Button'
import { Input } from '@/app/components/Form'
import { TrackingLocation } from '@/app/utils/analytics'
import { isProductionDomain } from '@/app/utils/captcha'
import { cn } from '@/app/utils/style'

type LeadFormProps = {
  tracking: TrackingLocation
  alignment?: 'left' | 'center'
  className?: string
}

type LeadFormData = {
  email: string
}

export function LeadForm({ tracking, alignment = 'left', className }: LeadFormProps) {
  const turnstileRef = useRef<TurnstileInstance>(null)
  const captchaSiteKey = process.env.NEXT_PUBLIC_CF_CAPTCHA_SITE_KEY
  const [isCaptchaEnabled, setIsCaptchaEnabled] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    setIsCaptchaEnabled(isProductionDomain())
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LeadFormData>({
    mode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  })

  const cleanAlignment = stegaClean(alignment) || 'left'

  const alignmentClasses = {
    left: 'items-start text-left',
    center: 'items-center text-center mx-auto',
  }

  const resetCaptcha = () => {
    setCaptchaToken('')
    turnstileRef.current?.reset()
  }

  const onSubmit = async (data: LeadFormData) => {
    setSubmitError('')

    try {
      const result = await subscribeToNewsletter({
        email: data.email,
        captchaToken,
      })

      if (result.success) {
        setIsSubmitted(true)
        resetCaptcha()
      } else {
        setSubmitError(result.error || 'Failed to subscribe. Please try again.')
        resetCaptcha()
      }
    } catch (error) {
      console.error('Subscription error:', error)
      setSubmitError('Failed to subscribe. Please try again.')
      resetCaptcha()
    }
  }

  if (isSubmitted) {
    return (
      <p
        className={cn(
          'size-body-xs md:size-body-sm text-white-900 max-w-[514px]',
          alignmentClasses[cleanAlignment],
          className,
        )}
      >
        Thanks for subscribing!
      </p>
    )
  }

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      className={cn(
        'w-full max-w-[514px] flex flex-col gap-lg',
        alignmentClasses[cleanAlignment],
        className,
      )}
    >
      <div className="flex flex-row xs:flex-col xs:gap-md gap-xl xs:items-stretch items-start w-full">
        <Input
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Please enter a valid email address',
            },
          })}
          id="email-lead-form"
          type="email"
          error={errors.email?.message || submitError}
          disabled={isSubmitting}
          placeholder="Enter your email"
          variant="small"
        />
        <Button
          button={{
            _type: 'button',
            variant: 'primary',
            size: 'md',
            link: undefined,
            icon: undefined,
            iconPlacement: 'none',
            dataId: null,
          }}
          className={cn(buttonSizeClasses({ lg: 'md' }), 'xs:w-full w-auto')}
          tracking={tracking}
          type="submit"
          disabled={isSubmitting || (isCaptchaEnabled && !captchaToken)}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </div>
      {isCaptchaEnabled && captchaSiteKey && (
        <Turnstile
          ref={turnstileRef}
          siteKey={captchaSiteKey}
          onSuccess={setCaptchaToken}
          onError={() => setCaptchaToken('')}
          onExpire={() => setCaptchaToken('')}
          options={{
            theme: 'light',
            size: 'flexible',
            appearance: 'interaction-only',
          }}
        />
      )}
      <p
        className={cn(
          'size-body-xs md:size-body-sm text-white-900',
          cleanAlignment === 'center' && 'text-center',
        )}
      >
        By subscribing you accept our{' '}
        <Link
          href="/publisher-terms"
          className="text-green-600 underline"
          target="_blank"
          rel="noreferrer nofollow"
        >
          Terms
        </Link>{' '}
        and{' '}
        <Link
          href="/privacy"
          className="text-green-600 underline"
          target="_blank"
          rel="noreferrer nofollow"
        >
          Privacy Policy
        </Link>
        .
      </p>
    </form>
  )
}
